import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail, Award } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Carousel from "@/components/Carousel";
import { useLang } from "@/i18n";

const pub = (f) => `${process.env.PUBLIC_URL}/${f}`;

// Taurus Group — Commercial Real Estate. Its own architectural identity, distinct
// from the group's gold-on-black and Corsa's burgundy: a cold anthracite / rat-grey
// palette, white serif display, platinum hairlines and JetBrains-Mono spec figures.
const DIPLO_GALLERY = [1, 2, 3, 4, 5].map((n) => pub(`DiplomaticBusinessCenterP${n}.jpg`));

// Small platinum overline with a leading hairline — the page's recurring label.
function Overline({ children, center = false, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.34em] text-white/45 ${className}`}
    >
      <span className="h-px w-8 bg-white/25" />
      {children}
      {center && <span className="h-px w-8 bg-white/25" />}
    </span>
  );
}

// Mono key/value spec list.
function Specs({ items }) {
  return (
    <dl className="border-t border-white/10">
      {items.map((s) => (
        <div key={s.k} className="flex items-baseline justify-between gap-4 border-b border-white/10 py-3">
          <dt className="font-rate text-[11px] uppercase tracking-[0.2em] text-white/40">{s.k}</dt>
          <dd className="text-right font-rate text-sm text-white/85">{s.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function CommercialPage() {
  const { t } = useLang();
  const tt = t.taurus;
  const navigate = useNavigate();
  const d = tt.diplomatic;
  const f = tt.ferdinand;
  const m = tt.mamaia;
  const dc = tt.dacia;
  const byKey = (k) => tt.buildings.find((b) => b.k === k);
  const dipBuilding = byKey("diplomatic");

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.body.classList.add("taurus-scroll");
    return () => document.body.classList.remove("taurus-scroll");
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-[#1b1d20] text-white">
      {/* ───────────────────────── HERO — huge logo ───────────────────────── */}
      <section className="relative flex flex-col items-center overflow-hidden bg-[#16181a] px-6 pb-14 pt-28 text-center sm:pb-20 sm:pt-36">
        {/* blueprint vertical hairlines */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.045) 0, rgba(255,255,255,0.045) 1px, transparent 1px, transparent 9%)",
          }}
        />
        {/* soft light from the top */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_0%,rgba(255,255,255,0.07),transparent_60%)]"
        />

        <div className="relative flex flex-col items-center">
          <Reveal delay={0.12}>
            <img
              src={pub("TaurusLogoWhite.svg")}
              alt={tt.brand}
              className="w-[min(62vw,260px)] drop-shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            />
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mx-auto mt-3 h-px w-14 bg-white/20 sm:mt-5" />
            <p className="mx-auto mt-3 max-w-xl font-serif text-xl font-light leading-relaxed text-white/75 sm:mt-5 sm:text-2xl">
              {tt.heroLead}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ──────────────────── PORTFOLIO — four landmarks ──────────────────── */}
      <section className="section-pad bg-[#1b1d20]">
        <div className="container-luxe">
          <Reveal className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <Overline className="mb-5">{tt.portfolioOverline}</Overline>
              <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
                {tt.portfolioTitle}
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">{tt.portfolioLead}</p>
          </Reveal>

          <div className="border-b border-white/10">
            {tt.buildings.map((b) => {
              const linked = Boolean(b.href);
              const Tag = linked ? "a" : "div";
              return (
                <Reveal key={b.k}>
                  <Tag
                    {...(linked ? { href: b.href } : {})}
                    className={`group block border-t border-white/10 ${linked ? "cursor-pointer" : ""}`}
                  >
                    <div className="grid grid-cols-[2.5rem_1fr] items-start gap-x-5 py-7 transition-transform duration-500 md:grid-cols-[4rem_1fr_auto] md:items-center md:gap-x-10 md:group-hover:translate-x-2">
                      <span className="font-rate text-base text-white/35 md:text-lg">{b.index}</span>

                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-serif text-2xl text-white/90 transition-colors duration-300 group-hover:text-white sm:text-3xl">
                            {b.name}
                          </h3>
                          {linked && (
                            <ArrowUpRight
                              size={20}
                              className="-translate-x-1 text-white/40 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:text-white group-hover:opacity-100"
                            />
                          )}
                        </div>
                        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/50">{b.tag}</p>
                        <p className="mt-2.5 font-rate text-[11px] uppercase tracking-[0.2em] text-white/30">
                          {b.location}
                        </p>
                      </div>

                      <div className="hidden md:col-start-3 md:flex md:flex-col md:items-end">
                        <span className="font-rate text-sm text-white/70">{b.year}</span>
                      </div>
                    </div>
                  </Tag>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════ DIPLOMATIC BUSINESS CENTER ════════════════════ */}
      {/* Full-screen video */}
      <section id="diplomatic" className="relative scroll-mt-0">
        <div className="relative h-[100svh] w-full overflow-hidden bg-black">
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={pub("DiplomaticBusinessCenterPoster.jpg")}
          >
            <source src={pub("DiplomaticBusinessCenter.mp4")} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#16181a] via-[#16181a]/15 to-[#16181a]/45" />

          <div className="absolute inset-x-0 bottom-0">
            <div className="container-luxe pb-12 md:pb-16">
              <Reveal>
                <Overline className="mb-5">
                  {dipBuilding.index} — {d.tag}
                </Overline>
                <h2 className="max-w-3xl font-serif text-[clamp(2.4rem,7vw,5.5rem)] font-light leading-[0.98] text-white">
                  {d.name}
                </h2>
                <p className="mt-6 font-rate text-[11px] uppercase tracking-[0.22em] text-white/55">
                  {dipBuilding.year} &nbsp;·&nbsp; {dipBuilding.regime} &nbsp;·&nbsp; {dipBuilding.area} &nbsp;·&nbsp;{" "}
                  {dipBuilding.location}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Diplomatic — editorial text */}
      <section className="section-pad bg-[#1b1d20]">
        <div className="container-luxe grid gap-10 md:grid-cols-12 md:gap-14">
          <div className="md:col-span-5 lg:col-span-4">
            <Reveal>
              <p className="font-serif text-2xl font-light leading-snug text-white sm:text-3xl">{d.lead}</p>
              <div className="mt-8">
                <Specs items={d.specs} />
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7 lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <div className="space-y-6 text-base leading-relaxed text-white/65 md:text-lg">
                {d.body.map((p, k) => (
                  <p key={k}>{p}</p>
                ))}
              </div>
              <div className="mt-9">
                <span className="font-rate text-[11px] uppercase tracking-[0.2em] text-white/40">
                  {d.tenantsLabel}
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {d.tenants.map((x) => (
                    <span
                      key={x}
                      className="border border-white/15 px-3 py-1.5 font-rate text-[11px] uppercase tracking-[0.18em] text-white/75"
                    >
                      {x}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Diplomatic — gallery carousel */}
      <section className="bg-[#1b1d20] pb-16 md:pb-24">
        <div className="container-luxe">
          <Reveal className="mb-8">
            <Overline>{d.galleryOverline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <Carousel images={DIPLO_GALLERY} alt={d.name} aspect="aspect-[16/9]" />
          </Reveal>
        </div>
      </section>

      {/* ════════════════════ 46 FERDINAND BUSINESS CENTER ════════════════════ */}
      <section id="ferdinand" className="section-pad scroll-mt-24 bg-[#16181a]">
        <div className="container-luxe grid items-start gap-8 md:grid-cols-2 md:gap-16">
          {/* Mobile-only header — sits above the photo */}
          <Reveal className="md:hidden">
            <Overline className="mb-4">
              {byKey("ferdinand").index} — {f.tag}
            </Overline>
            <h2 className="font-serif text-4xl font-light leading-[1.05] text-white">{f.name}</h2>
          </Reveal>

          {/* Left column — exterior building photo with the interior stacked beneath */}
          <Reveal className="space-y-6">
            <div className="relative overflow-hidden border border-white/12">
              <img
                src={pub("FerdinandBusinessCenter.jpeg")}
                alt={f.name}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute left-4 top-4 flex items-center gap-2 border border-white/25 bg-black/40 px-3 py-1.5 backdrop-blur-sm">
                <Award size={14} className="text-white/80" />
                <span className="font-rate text-[10px] uppercase tracking-[0.18em] text-white/85">
                  {f.tag}
                </span>
              </div>
            </div>

            <div className="relative overflow-hidden border border-white/12">
              <img
                src={pub("FerdinandInterior.jpeg")}
                alt={`${f.name} interior`}
                loading="lazy"
                className="block w-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              <div className="absolute left-4 top-4 border border-white/25 bg-black/40 px-3 py-1.5 backdrop-blur-sm">
                <span className="font-rate text-[10px] uppercase tracking-[0.22em] text-white/85">
                  Interior
                </span>
              </div>
            </div>
          </Reveal>

          {/* Text */}
          <Reveal delay={0.1}>
            {/* Desktop-only header (mobile shows it above the photo) */}
            <div className="hidden md:block">
              <Overline className="mb-5">
                {byKey("ferdinand").index} — {f.tag}
              </Overline>
              <h2 className="font-serif text-[clamp(2.1rem,5vw,3.6rem)] font-light leading-[1.02] text-white">
                {f.name}
              </h2>
            </div>
            <p className="mt-1 flex items-start gap-2.5 font-rate text-[11px] uppercase tracking-[0.14em] text-white/45 md:mt-4">
              <Award size={14} className="mt-px shrink-0 text-white/55" />
              <span className="[text-wrap:pretty]">{f.award}</span>
            </p>
            <p className="mt-7 font-serif text-xl font-light italic leading-snug text-white/80">{f.lead}</p>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-white/65">
              {f.body.map((p, k) => (
                <p key={k}>{p}</p>
              ))}
            </div>
            <div className="mt-8 max-w-sm">
              <Specs items={f.specs} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════ 220 MAMAIA BUSINESS CENTER ════════════════════ */}
      <section id="mamaia" className="section-pad scroll-mt-24 bg-[#1b1d20]">
        <div className="container-luxe">
          <Reveal className="mb-8 md:mb-10">
            <Overline className="mb-5">
              {byKey("mamaia").index} — {m.tag}
            </Overline>
            <h2 className="font-serif text-[clamp(2.1rem,5vw,3.6rem)] font-light leading-[1.02] text-white">
              {m.name}
            </h2>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="overflow-hidden border border-white/12">
              <img
                src={pub("TaurusTOLDIMED.jpeg")}
                alt={m.name}
                loading="lazy"
                className="block w-full"
              />
            </div>
          </Reveal>

          <div className="mt-10 grid gap-10 md:mt-12 md:grid-cols-12 md:gap-14">
            <div className="md:col-span-5 lg:col-span-4">
              <Reveal>
                <p className="font-serif text-2xl font-light leading-snug text-white sm:text-3xl">{m.lead}</p>
                <div className="mt-8">
                  <Specs items={m.specs} />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-7 lg:col-span-7 lg:col-start-6">
              <Reveal delay={0.1}>
                <div className="space-y-6 text-base leading-relaxed text-white/65 md:text-lg">
                  {m.body.map((p, k) => (
                    <p key={k}>{p}</p>
                  ))}
                </div>
                <div className="mt-9">
                  <span className="font-rate text-[11px] uppercase tracking-[0.2em] text-white/40">
                    {m.tenantLabel}
                  </span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m.tenants.map((x) => (
                      <span
                        key={x}
                        className="border border-white/15 px-3 py-1.5 font-rate text-[11px] uppercase tracking-[0.18em] text-white/75"
                      >
                        {x}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ DACIA BUSINESS CENTER ════════════════════ */}
      <section id="dacia" className="section-pad scroll-mt-24 bg-[#16181a]">
        <div className="container-luxe">
          <Reveal className="mb-8 md:mb-10">
            <Overline className="mb-5">
              {byKey("dacia").index} — {dc.tag}
            </Overline>
            <h2 className="font-serif text-[clamp(2.1rem,5vw,3.6rem)] font-light leading-[1.02] text-white">
              {dc.name}
            </h2>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="overflow-hidden border border-white/12">
              <img
                src={pub("daciaBusinessCenter2.jpeg")}
                alt={dc.name}
                loading="lazy"
                className="block w-full"
              />
            </div>
          </Reveal>

          <div className="mt-10 grid gap-10 md:mt-12 md:grid-cols-12 md:gap-14">
            <div className="md:col-span-5 lg:col-span-4">
              <Reveal>
                <p className="font-serif text-2xl font-light leading-snug text-white sm:text-3xl">{dc.lead}</p>
                <div className="mt-8">
                  <Specs items={dc.specs} />
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-7 lg:col-span-7 lg:col-start-6">
              <Reveal delay={0.1}>
                <div className="space-y-6 text-base leading-relaxed text-white/65 md:text-lg">
                  {dc.body.map((p, k) => (
                    <p key={k}>{p}</p>
                  ))}
                </div>
                <div className="mt-9">
                  <span className="font-rate text-[11px] uppercase tracking-[0.2em] text-white/40">
                    {dc.tenantLabel}
                  </span>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {dc.tenants.map((x) => (
                      <span
                        key={x}
                        className="border border-white/15 px-3 py-1.5 font-rate text-[11px] uppercase tracking-[0.18em] text-white/75"
                      >
                        {x}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── CONTACT / back ───────────────────────── */}
      <section className="section-pad border-t border-white/10 bg-[#1b1d20]">
        <div className="container-luxe">
          <Reveal className="mx-auto max-w-xl text-center">
            <Overline center className="mb-6 justify-center">
              {tt.contactOverline}
            </Overline>
            <h2 className="font-serif text-3xl font-light text-white sm:text-4xl">{tt.contactTitle}</h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-white/55 md:text-base">
              {tt.contactText}
            </p>
            <a
              href={`mailto:${tt.contactEmail}`}
              className="mt-7 inline-flex items-center gap-2 font-rate text-sm tracking-[0.05em] text-white/85 transition-colors hover:text-white"
            >
              <Mail size={16} className="text-white/55" /> {tt.contactEmail}
            </a>

            <div className="mt-12">
              <button
                onClick={backHome}
                className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 font-rate text-xs uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-white hover:bg-white hover:text-[#16181a]"
              >
                <ArrowLeft size={16} /> {tt.back}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
